import { Component } from '@theme/component';
import { createTextReveal, createRevealScheduler, bandProgress } from '@theme/text-reveal';

class EditorialText extends Component {
  #reveal = null;
  #scheduler = null;

  // Layout cache — filled in #measure(), never read during scroll.
  #topPx = 0;
  #height = 0;
  #viewportHeight = 0;
  #lastScrollY = null;
  #band = {};

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  // A re-render leaves the shadow holding the old letters, and the pinned section owns the reveal
  // that would rebuild it, so hand the work there rather than standing down and leaving it stale.
  updatedCallback() {
    super.updatedCallback?.();
    this.#teardown();

    const pinnedSection = this.closest('scroll-reading-text');
    if (pinnedSection) {
      pinnedSection.updatedCallback();
      return;
    }

    this.#setup();
  }

  // Stands down inside the pinned section, which owns the reveal for every block it contains.
  // A page can hold both, and the custom element upgrades every tag on it either way.
  #setup() {
    if (this.closest('scroll-reading-text')) return;

    this.#lastScrollY = null;
    this.#band = this.#bandRatios();
    const startOpacity = parseFloat(this.dataset.startOpacity) || 0;
    this.#reveal = createTextReveal(this, { startOpacity });
    if (!this.#reveal.segmentCount) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#reveal.paint(1);
      return;
    }

    this.#measure();

    this.#scheduler = createRevealScheduler(this, {
      onMeasure: () => {
        this.#measure();
        this.#update(true);
      },
      onFrame: this.#update,
      gateOnVisibility: true,
    });
    this.#scheduler.start(this.abortController.signal);
    this.#update();
  }

  #teardown() {
    this.#scheduler?.stop();
    this.#scheduler = null;
    this.#reveal = null;
  }

  // Reads the scroll speed setting as the two ends of the reveal band, leaving either out to
  // bandProgress when the block carries no usable value of its own.
  #bandRatios() {
    const startRatio = parseFloat(this.dataset.bandStart);
    const endRatio = parseFloat(this.dataset.bandEnd);
    return {
      startRatio: Number.isFinite(startRatio) ? startRatio : undefined,
      endRatio: Number.isFinite(endRatio) ? endRatio : undefined,
    };
  }

  // Caches the page offset instead of the viewport rect, so #update() never forces a reflow. Only
  // the offset comes from the anchor; the band is paced by the block's own height either way.
  #measure() {
    this.#reveal?.measure();
    this.#topPx = this.#anchor().getBoundingClientRect().top + window.scrollY;
    this.#height = this.getBoundingClientRect().height;
    this.#viewportHeight = window.innerHeight;
  }

  // A pinned box stops moving with the page for as long as it is stuck, so a reveal that paces
  // itself off one either freezes or starts out already finished. Paces off the nearest box that
  // still scrolls instead, which for anything unpinned is the block itself.
  #anchor() {
    let anchor = this;

    for (let node = this; node && node !== document.body; node = node.parentElement) {
      if (getComputedStyle(node).position === 'sticky') anchor = node.parentElement ?? anchor;
    }

    return anchor;
  }

  // Skips frames the page did not move through, unless `force` says the answer changed anyway.
  #update = (force = false) => {
    if (!this.#reveal) return;
    const scrollY = window.scrollY;
    if (!force && scrollY === this.#lastScrollY) return;
    this.#lastScrollY = scrollY;

    this.#reveal.paint(
      bandProgress({
        top: this.#topPx - scrollY,
        height: this.#height,
        viewportHeight: this.#viewportHeight,
        ...this.#band,
      })
    );
  };
}

if (!customElements.get('editorial-text')) {
  customElements.define('editorial-text', EditorialText);
}
